import { useMutation, useQueryClient } from 'react-query';

import { ResolveSummaryResponse } from '../../../../backend/src/matter/procedural-timeline/dto/resolve-summary.response.dto';
import type { ProceduralTimelineEventSuggestion } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

type Action = 'accept' | 'reject';

const TRACKING_EVENT: Record<Action, string> = {
  accept: TrackingEventsV2.ProceduralTimelineSuggestionAccept,
  reject: TrackingEventsV2.ProceduralTimelineSuggestionReject,
};

interface Params {
  matterId: string;
  suggestionId: string;
}

function useSuggestionAction(action: Action) {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<ResolveSummaryResponse, Error, Params>(
    async ({ matterId, suggestionId }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/suggestions/${suggestionId}/${action}`,
        getFetchConfig({ method: FetchMethod.POST, data: {} }),
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || `Failed to ${action} suggestion`);
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId, suggestionId }) => {
        // Drop the resolved suggestion from the cache synchronously so its row
        // animates out immediately, rather than flickering through the refetch
        // round trip while the events list updates on its own schedule.
        queryClient.setQueryData<ProceduralTimelineEventSuggestion[]>(
          [QueryEntity.ProceduralTimelineSuggestions, { matterId }],
          (current) => (current ?? []).filter((s) => s.id !== suggestionId),
        );
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineSuggestions, { matterId }]);
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
        // Resolving the last pending suggestion unblocks uploads — refresh
        // eligibility so the Upload Timetable button re-enables right away.
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineUploadEligibility, { matterId }]);
        trackCustomEvent(TRACKING_EVENT[action], { matterId, suggestionId });
      },
    },
  );
}

export const useAcceptProceduralSuggestion = () => useSuggestionAction('accept');
export const useRejectProceduralSuggestion = () => useSuggestionAction('reject');
